import React, { useState } from "react";
import "react-responsive-modal/styles.css";
import { Modal } from "react-responsive-modal";
import { MdSend, MdAttachFile, MdClose, MdDelete } from "react-icons/md";
import { CircularProgress } from "@mui/material";
import toast from "react-hot-toast";
import axios from "axios";

// Assuming we can pass isDarkMode here or default to light, user requested premium UI.
// Sidebar passes open3 etc to Reply component, but checking Sidebar.jsx from memory it doesn't pass isDarkMode to Reply yet.
// I will assume light/dark props aren't there yet so I'll use a neutral premium theme or try to use same logic as Compose.
// Ideally Sidebar should pass isDarkMode. I will handle that in next step.

function Reply({ open3, handleClose, reply_mail, handleBackInbox, isDarkMode }) {
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);

  const theme = {
    bg: isDarkMode ? "#1a1a1a" : "#ffffff",
    text: isDarkMode ? "#e0e0e0" : "#333333",
    inputBg: isDarkMode ? "#2d2d2d" : "#f5f5f5",
    border: isDarkMode ? "#333333" : "#e0e0e0",
    accent: isDarkMode ? "#4dabf5" : "#007bff",
    cardBg: isDarkMode ? "#252525" : "#ffffff",
    secondaryText: isDarkMode ? "#aaaaaa" : "#666666",
    hover: isDarkMode ? "#2d2d2d" : "#f5f5f5",
    fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif"
  };

  const handleDeleteFile = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments((prev) => [...prev, ...files]);
  };

  const handleSend = async () => {
    try {
      setLoading(true);
      // Use hardcoded URL if env var is risky, but user's code had env.
      // Based on urls.py verify: path('send_reply/', views.send_reply)
      const baseUrl = 'https://casualfootwears.com/api/send_reply/';

      const formData = new FormData();
      formData.append("mail_body", body);
      formData.append("t_id", reply_mail.thread_id);
      formData.append("selected_mail", reply_mail.selected_mail);
      formData.append("target_sub", reply_mail.subject);
      formData.append("target_address", reply_mail.from_email || reply_mail.from); // Use parsed email if available

      attachments.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file);
      });

      await axios.post(baseUrl, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      handleClose();
      setBody("");
      setAttachments([]);
      setLoading(false);
      toast.success("Reply sent successfully");
    } catch (error) {
      setLoading(false);
      toast.error("Failed to send reply");
      console.error(error);
    }
  };

  return (
    <Modal
      open={open3}
      onClose={handleClose}
      center
      classNames={{ modal: "customModal" }}
      styles={{
        modal: {
          maxWidth: "800px",
          width: "95%",
          padding: "0",
          borderRadius: "16px",
          backgroundColor: theme.cardBg,
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          fontFamily: theme.fontFamily
        },
        overlay: {
          background: "rgba(0, 0, 0, 0.7)",
          backdropFilter: "blur(4px)"
        }
      }}
      showCloseIcon={false}
    >
      <div style={{ padding: "24px", color: theme.text }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <h2 style={{ margin: 0, fontSize: "20px", fontWeight: "700" }}>Reply</h2>
            <span style={{ fontSize: "13px", color: theme.secondaryText, marginTop: "4px" }}>
              Replying to: <strong>{reply_mail?.from_name || reply_mail?.from}</strong>
            </span>
          </div>

          <button
            onClick={handleClose}
            style={{ background: "none", border: "none", color: theme.text, cursor: "pointer", padding: "5px", display: "flex" }}
          >
            <MdClose size={24} />
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
          {/* Subject Preview */}
          <div style={{ padding: "10px", backgroundColor: theme.inputBg, borderRadius: "8px", fontSize: "14px", color: theme.secondaryText }}>
            Subject: {reply_mail?.subject}
          </div>

          {/* Body Field */}
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write your reply..."
            style={{
              width: "100%",
              minHeight: "250px",
              padding: "15px",
              borderRadius: "8px",
              border: `1px solid ${theme.border}`,
              backgroundColor: "transparent",
              color: theme.text,
              fontSize: "15px",
              fontFamily: theme.fontFamily,
              resize: "vertical",
              outline: "none",
              lineHeight: "1.5"
            }}
            onFocus={(e) => e.target.style.borderColor = theme.accent}
            onBlur={(e) => e.target.style.borderColor = theme.border}
          />

          {/* Attachments Section */}
          {attachments.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", padding: "10px", backgroundColor: theme.inputBg, borderRadius: "8px" }}>
              {attachments.map((file, index) => (
                <div key={index} style={{
                  display: "flex", alignItems: "center", gap: "8px",
                  backgroundColor: theme.cardBg, padding: "6px 10px",
                  borderRadius: "6px", border: `1px solid ${theme.border}`, fontSize: "13px"
                }}>
                  <span style={{ maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</span>
                  <MdClose size={16} style={{ cursor: "pointer", color: theme.secondaryText }} onClick={() => handleDeleteFile(index)} />
                </div>
              ))}
            </div>
          )}

          {/* Footer Actions */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "10px" }}>
            <div>
              <input type="file" id="reply-file-input" multiple style={{ display: "none" }} onChange={handleFileChange} />
              <label htmlFor="reply-file-input">
                <span style={{
                  display: "flex", alignItems: "center", gap: "6px", cursor: "pointer",
                  color: theme.text, padding: "8px 12px", borderRadius: "8px", backgroundColor: theme.hover
                }}>
                  <MdAttachFile size={20} /> <span style={{ fontSize: "14px", fontWeight: "500" }}>Attach files</span>
                </span>
              </label>
            </div>

            <button
              onClick={handleSend}
              disabled={loading}
              style={{
                padding: "10px 24px", borderRadius: "30px", border: "none",
                backgroundColor: theme.accent, color: "#fff", cursor: loading ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", gap: "8px", fontWeight: "600", fontSize: "15px",
                boxShadow: "0 4px 10px rgba(0,0,0,0.2)", opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? <CircularProgress size={20} style={{ color: "#fff" }} /> : <><MdSend size={18} /> Send Reply</>}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default Reply;
